import { Component, Input } from '@angular/core';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';

/**
 * Full-screen loading overlay with a spinner, title, and message, used during initial app
 * bootstrap and route transitions.
 *
 * Issue #1966: this is the FIRST thing every reader sees, and in the dark scheme it was the
 * clearest example of the failure the issue is about. The card is a `mat-card`, so its surface
 * follows Material's own tokens and went to `#424242` — while this file held the title at 87%
 * black and the message at 60% black. Near-black on `#424242` measures about 1.4:1: the words
 * "Loading..." were invisible on the boot screen of the scheme the new control offers.
 *
 * The sheet behind the card had the mirror-image problem, a hardcoded 80% WHITE veil laid over a
 * dark page.
 */
@Component({
  selector: 'app-loading',
  imports: [MatProgressSpinnerModule, MatCardModule],
  template: `
    <div class="loading-container">
      <mat-card class="loading-card">
        <mat-card-content>
          <div class="loading-content">
            <mat-spinner diameter="40"></mat-spinner>
            <h3>{{ title }}</h3>
            <p class="loading-message">{{ message }}</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  `,
  styles: [
    `
      .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        /* The veil over whatever the overlay is covering; 80% of the scheme's own surface. */
        background-color: color-mix(in srgb, var(--mat-sys-surface) 80%, transparent);
      }

      .loading-card {
        max-width: 400px;
        text-align: center;
        border-radius: 16px !important;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
      }

      .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 16px;
      }

      .loading-message {
        color: var(--mat-sys-on-surface-variant);
        margin: 0;
      }

      h3 {
        margin: 0;
        color: var(--mat-sys-on-surface);
        font-weight: 500;
      }
    `,
  ],
})
export class LoadingComponent {
  @Input() title: string = 'Loading...';
  @Input() message: string = 'Please wait.';
}
